import { getFields, cleanVoucherSubTypeCode } from '../../helpers/initialFormState';
import { CompensationVoucher, CompensationVoucherHistory, CompensationVouchersHistoryObj, Field, ValidationError } from '../../models/home';
import { DEFAULT_BOOKING_INFORMATION_FIELDS, DEFAULT_COMPENSATION_FIELDS, DEFAULT_FLIGHT_INFORMATION_FIELDS, DEFAULT_PASSENGER_INFORMATION_FIELDS } from '../../constants/default';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { error } from '../../helpers/apiConfig';
import { FIELD_NAMES, REGEX, WIFI_REGION_MAPPING } from '../../constants/common';
import { formatDateInYYYYMMDD } from '../../helpers/date';
import { GET_VOUCHER_CODES } from '../../queries/common';
import { GROUPS, getGroupFromRoles } from '../../constants/authGroups';
import { MUTATE_COMPENSATION_CODES, MUTATE_OFFLINE_MODULE } from '../../mutations/common';
import { useApiError } from '../../providers/ApiErrorProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useMasterData } from '../../providers/MasterDataProvider';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { VoucherIdObjInterface } from '../../models/app';
import { v4 as uuidv4 } from 'uuid';


interface Props {
    compensationVouchers: CompensationVoucher[];
    isDeviceOffline: boolean;
    setCompensationVouchers: Dispatch<SetStateAction<CompensationVoucher[]>>;
    setShowConfirmation: Dispatch<SetStateAction<boolean>>;
    setValidationError: Dispatch<SetStateAction<ValidationError>>;
}

export const useCompensationRequestForm = ({
    compensationVouchers,
    isDeviceOffline,
    setCompensationVouchers,
    setShowConfirmation,
    setValidationError
}: Props) => {
    const authContext = useAuthProvider();
    const { setErrorCode } = useApiError();
    const { masterData } = useMasterData();
    const { i18n, t } = useTranslation();

    const [bookingInformationFields, setBookingInformationFields] = useState<Field[]>(JSON.parse(JSON.stringify(DEFAULT_BOOKING_INFORMATION_FIELDS)));
    const [compensationFields, setCompensationFields] = useState<Field[]>(JSON.parse(JSON.stringify(DEFAULT_COMPENSATION_FIELDS)));
    const [flightInformationFields, setFlightInformationFields] = useState<Field[]>(JSON.parse(JSON.stringify(DEFAULT_FLIGHT_INFORMATION_FIELDS)));
    const [isFlightUser, setIsFlightUser] = useState<boolean>(false);
    const [isVoucherCodeQueryTriggered, setVoucherCodeQueryTriggered] = useState<boolean>(false);
    const [loadingRetry, setLoadingRetry] = useState(false);
    const [mutationData, setMutationData] = useState<{
        stkAlocateVoucher?: {
            code: number;
            message: string;
            data: string;
        };
    }>({});
    const [passengerInformationFields, setPassengerInformationFields] = useState<Field[]>(JSON.parse(JSON.stringify(DEFAULT_PASSENGER_INFORMATION_FIELDS)));
    const [resendErroredVoucherId, setResendErroredVoucherId] = useState('');
    const [retryCount, setRetryCount] = useState(0);
    const [showFlightNumberPlaceholder, setShowFlightNumberPlaceholder] = useState(false);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);
    const [voucherIds, setVoucherIds] = useState<string[]>([]);

    // Helper to get field value by name
    const getFieldValue = (fields: Field[], fieldName: string): string => {
        const field = fields.find(f => f.name === fieldName);
        return field?.value || '';
    };

    // Helper to get compensation field values
    const getCompensationFieldValue = (fieldName: string): string => {
        return getFieldValue(compensationFields, fieldName);
    };

    const getVoucherIssuanceReasonText = (): string => {
        const reasonId = getCompensationFieldValue(FIELD_NAMES.reasonId);
        const categoryId = getCompensationFieldValue(FIELD_NAMES.categoryId);
        const lang = i18n.language;

        const pickLocalized = (en?: string, fr?: string) =>
            lang === 'fr' ? (fr || en || '') : (en || fr || '');

        if (categoryId) {
            const category = masterData.categories.find(c => c.categoryId === categoryId);
            if (category) {
                return pickLocalized(category.categoryTitleEn, category.categoryTitleFr);
            }
        }
        if (reasonId) {
            const reason = masterData.reasons.find(r => r.reasonId === reasonId);
            if (reason) {
                return pickLocalized(reason.reasonTitleEn, reason.reasonTitleFr);
            }
        }
        return '';
    };

    const [myMutation, { 
        loading: loadingMutation, 
        error: errorMutation, 
        data: dataMutation
    }] = useMutation(MUTATE_COMPENSATION_CODES);

    const [offlineModuleMutation, { 
        loading: loadingOfflineModule, 
        error: errorOfflineModule, 
        data: dataOfflineModule 
    }] = useMutation(MUTATE_OFFLINE_MODULE);

    const { 
        loading: loadingVoucherCodes, 
        error: errorVoucherCodes, 
        data: dataVoucherCodes 
    } = useQuery(GET_VOUCHER_CODES, {
        variables: {
            requestId: voucherIds[0],
            voucherType: getCompensationFieldValue(FIELD_NAMES.compensationType) === 'Wi-Fi' ? 'WIFI' : 'NTP'
        },
        skip: !isVoucherCodeQueryTriggered || !voucherIds[0],
        fetchPolicy: "no-cache"
    });

    /* Custom hook to track the previous value of a variable */
    const usePrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };
    
    const previousLanguage = usePrevious(i18n.language);

    const [editFields] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem('HistoryEditFields') || '{}');
        } catch {
            return {};
        }
    });

    const getEditRequestId = () => {
        return editFields.requestId || undefined;
    };

    // Helper to get a valid requestId for edit/retry scenarios
    const getValidRequestId = () => {
        // Handles: undefined, null, '', '""', and whitespace-only
        if (
            typeof editFields.requestId === 'string' &&
            editFields.requestId.trim() !== '' &&
            editFields.requestId.trim() !== '""' &&
            editFields.requestId.trim().replace(/"/g, '') !== ''
        ) {
            return editFields.requestId;
        }
        return uuidv4();
    };

    const allocateNTPVoucherCodes = (payload) => {
        const performMutation = async (payload) => {
            try {
                const editRequestId = getValidRequestId();
                const { data } = await myMutation({
                    variables: {
                        payload: payload
                    }
                });
                if (data) {
                    if (data.stkAlocateVoucher.code === 6000) {
                        // Success case
                        setMutationData(data);
                        setShowConfirmation(true);
                        // Add success voucher to compensationVouchers
                        const successVoucher: CompensationVoucher = {
                            errorCode: 200,
                            errorText: '',
                            code: '',
                            type: getCompensationFieldValue(FIELD_NAMES.compensationType),
                            used: true,
                            user: {
                                firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                                lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                                flightMarketingCarrier: 'AC',
                                flightMarketingNumber: Number(flightInformationFields[0].value),
                                flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                                bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                                bookingPassengerEmailAddress: passengerInformationFields[2].value,
                                voucherIssuanceReason: getVoucherIssuanceReasonText()
                            }
                        };
                        setCompensationVouchers([successVoucher]);
                        // Add to local history
                        const historyVoucherObj: CompensationVoucherHistory = {
                            id: bookingInformationFields[0].value ? bookingInformationFields[0].value+Math.random() : bookingInformationFields[1].value+Math.random(),
                            firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            flightMarketingCarrier: 'AC',
                            flightMarketingNumber: Number(flightInformationFields[0].value),
                            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                            bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                            bookingPassengerEmailAddress: passengerInformationFields[2].value,
                            type: getCompensationFieldValue(FIELD_NAMES.compensationType),
                            voucherIssuanceReason: getVoucherIssuanceReasonText(),
                            voucherCodes: []
                        };
                        const compensationVouchersHistoryObj: CompensationVouchersHistoryObj = JSON.parse(localStorage.getItem('compensationVouchersHistoryObj') ?? '{}');
                        if (!compensationVouchersHistoryObj.compensationVouchersHistory || !Array.isArray(compensationVouchersHistoryObj.compensationVouchersHistory)) {
                            compensationVouchersHistoryObj.compensationVouchersHistory = [];
                        }
                        compensationVouchersHistoryObj.compensationVouchersHistory.push(historyVoucherObj);
                        localStorage.setItem('compensationVouchersHistoryObj', JSON.stringify(compensationVouchersHistoryObj));
                    } else {
                        // Error case
                        setValidationError({
                            isError: true,
                            errorCode: data.stkAlocateVoucher.code
                        });
                    }
                }
            } catch (e: any) {
                console.error("allocateNTPVoucherCodes error");
                setErrorCode(error.DEFAULT);
            }
        };

        const asyncOps = async () => {
            const refreshedToken = await authContext.acquireTokenSilently();
            if(refreshedToken) {
                performMutation(payload);
            }
        }
        asyncOps();
    };

    const allocateWIFIVoucherCodes = useCallback((voucherIdsMutationPayload) => {
        const performMutation = async (payload) => {
            try {
                const editRequestId = getValidRequestId();
                const reasonId = getCompensationFieldValue(FIELD_NAMES.reasonId);
                const categoryId = getCompensationFieldValue(FIELD_NAMES.categoryId);
                const cabinId = getCompensationFieldValue(FIELD_NAMES.cabinId);
                const compensationNum = getCompensationFieldValue(FIELD_NAMES.compensationNum);
                const comments = getCompensationFieldValue(FIELD_NAMES.comments);
                const wifiRegion = getCompensationFieldValue(FIELD_NAMES.wifiRegion);
                const { data } = await myMutation({
                    variables: {
                        payload: {
                            ...payload,
                            requestId: editRequestId,
                            requestType: 'ONLINE',
                            quantity: Number(compensationNum),
                            flightLegNumber: Number(flightInformationFields[0].value),
                            flightMarketingCarrier: 'AC',
                            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                            passengerEmailAddress: passengerInformationFields[2].value ? passengerInformationFields[2].value : 'test@c.com',
                            passengerFirstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            passengerLastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            pnrLocator: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                            voucherIssuanceComment: comments ? comments : 'Optional',
                            voucherIssuanceReason: getVoucherIssuanceReasonText(),
                            voucherType: 'WIFI',
                            voucherSubTypeCode: WIFI_REGION_MAPPING[wifiRegion],
                            categoryId: categoryId || undefined,
                            reasonId: reasonId || undefined,
                            cabinId: cabinId || undefined
                        },
                    },
                });
                if (data) {
                    if (data.stkAlocateVoucher.code !== 6000) {
                        // If error, show immediately and do not poll
                        setValidationError({
                            isError: true,
                            errorCode: data.stkAlocateVoucher.code
                        });
                        return;
                    }
                    // Only poll for voucher codes if success (code 6000)
                    let requestId = data.stkAlocateVoucher.data;
                    if (
                        !requestId ||
                        requestId === '{}' ||
                        requestId === '""' ||
                        (typeof requestId === 'string' && requestId.trim() === '')
                    ) {
                        requestId = editRequestId;
                    }
                    if (requestId && requestId !== '{}' && requestId !== '""' && requestId.trim() !== '') {
                        setVoucherIds([requestId]);
                        setVoucherCodeQueryTriggered(true);
                    }
                }
            } catch (e: any) {
                console.error("allocateWIFIVoucherCodes error");
                setErrorCode(error.DEFAULT);
            }
        };

        const asyncOps = async () => {
            const refreshedToken = await authContext.acquireTokenSilently();
            if (refreshedToken) {
                performMutation(voucherIdsMutationPayload);
            }
        }
        asyncOps();
    }, [authContext, myMutation, setErrorCode, compensationFields, flightInformationFields, passengerInformationFields, bookingInformationFields, setValidationError, setShowConfirmation]);

    const handleOnGenerateVoucherCode = () => {
        setRetryCount(0);
        setValidationError({
            errorCode: 0,
            isError: false
        });
        const compensationType = getCompensationFieldValue(FIELD_NAMES.compensationType);
        if (isDeviceOffline) {
            if (compensationType === 'Wi-Fi') {
                setValidationError({
                    isError: true,
                    errorCode: 4013
                });
            } else {
                handleOnNTPOfflineSupport();
            }
        } else {
            const reasonId = getCompensationFieldValue(FIELD_NAMES.reasonId);
            const categoryId = getCompensationFieldValue(FIELD_NAMES.categoryId);
            const cabinId = getCompensationFieldValue(FIELD_NAMES.cabinId);
            const compensationNum = getCompensationFieldValue(FIELD_NAMES.compensationNum);
            const comments = getCompensationFieldValue(FIELD_NAMES.comments);
            if (compensationType === 'Wi-Fi') {
                const wifiPayload = {
                    requestId: getValidRequestId(),
                    requestType: 'ONLINE',
                    quantity: Number(compensationNum),
                    flightLegNumber: Number(flightInformationFields[0].value),
                    flightMarketingCarrier: 'AC',
                    flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                    passengerEmailAddress: passengerInformationFields[2].value ? passengerInformationFields[2].value : 'test@c.com',
                    passengerFirstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                    passengerLastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                    pnrLocator: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                    voucherIssuanceComment: comments ? comments : 'Optional',
                    voucherIssuanceReason: getVoucherIssuanceReasonText(),
                    voucherType: 'WIFI',
                    categoryId: categoryId || undefined,
                    reasonId: reasonId || undefined,
                    cabinId: cabinId || undefined
                };
                allocateWIFIVoucherCodes(wifiPayload);
            } else {
                // Direct NTP request through backend
                const compensationPercentageSelected = compensationType.split(' ')[0];
                // Format voucherSubTypeCode as 'percent_15' format
                let voucherSubTypeCode = compensationPercentageSelected ? `percent_${compensationPercentageSelected.replace('%', '')}` : 'percent_NA';
                voucherSubTypeCode = cleanVoucherSubTypeCode(voucherSubTypeCode);
                const ntpPayload = {
                    requestId: getValidRequestId(),
                    requestType: 'ONLINE',
                    quantity: Number(compensationNum),
                    flightLegNumber: Number(flightInformationFields[0].value),
                    flightMarketingCarrier: 'AC',
                    flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                    passengerEmailAddress: passengerInformationFields[2].value ? passengerInformationFields[2].value : 'test@c.com',
                    passengerFirstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                    passengerLastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                    pnrLocator: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                    voucherIssuanceComment: comments ? comments : 'Optional',
                    voucherIssuanceReason: getVoucherIssuanceReasonText(),
                    voucherType: 'NTP',
                    voucherSubTypeCode: voucherSubTypeCode,
                    categoryId: categoryId || undefined,
                    reasonId: reasonId || undefined,
                    cabinId: cabinId || undefined
                };
                allocateNTPVoucherCodes(ntpPayload);
            }
        }
    };

    const getWifiVoucherCodes = useCallback(() => {
        const asyncOps = async () => {
            const refreshedToken = await authContext.acquireTokenSilently();
            if (refreshedToken) {
                setVoucherCodeQueryTriggered(true);
            }
        }
        asyncOps();
    }, [authContext]);

    const handleOnClickClear = useCallback(() => {
        sessionStorage.clear();
        setFlightInformationFields([
            {name: FIELD_NAMES.flightNumber, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.date, value: '', error: '', requiredError: ''}
        ]);
        setBookingInformationFields([
            {name: FIELD_NAMES.bookingReference, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.ticketNumber, value: '', error: '', requiredError: ''}
        ]);
        setPassengerInformationFields([
            {name: FIELD_NAMES.firstName, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.lastName, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.email, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.confirmEmail, value: '', error: '', requiredError: ''}
        ]);
        setCompensationFields([
            {name: FIELD_NAMES.reasonId, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.categoryId, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.cabinId, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.compensationNum, value: '1', error: '', requiredError: '', disabled: false},
            {name: FIELD_NAMES.comments, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.compensationType, value: '', error: '', requiredError: ''},
            {name: FIELD_NAMES.wifiRegion, value: '', error: '', requiredError: ''}
        ]);
        setShowFlightNumberPlaceholder(false);
        setResendErroredVoucherId('');
        setValidationError({
            errorCode: 0,
            isError: false
        });
    }, [setValidationError]);
    
    const handleOnClickSubmit = () => {
        sessionStorage.clear();
        if (!validateFormFieldsNoError()) return;
        handleOnGenerateVoucherCode();
        sessionStorage.setItem('RecentFormValues', JSON.stringify({
            [FIELD_NAMES.flightNumber]: flightInformationFields[0].value,
            [FIELD_NAMES.date]: flightInformationFields[1].value,
            [FIELD_NAMES.bookingReference]: bookingInformationFields[0].value,
            [FIELD_NAMES.ticketNumber]: bookingInformationFields[1].value,
            [FIELD_NAMES.firstName]: passengerInformationFields[0].value,
            [FIELD_NAMES.lastName]: passengerInformationFields[1].value,
            [FIELD_NAMES.email]: passengerInformationFields[2].value,
            [FIELD_NAMES.confirmEmail]: passengerInformationFields[3].value
        }));
    };

    const handleOnNTPOfflineSupport = () => {
        // Create a flat request object with all fields at the root level (mirror ONLINE fields)
        const compensationType = getCompensationFieldValue(FIELD_NAMES.compensationType);
        const compensationNum = getCompensationFieldValue(FIELD_NAMES.compensationNum);
        const comments = getCompensationFieldValue(FIELD_NAMES.comments);
        const reasonId = getCompensationFieldValue(FIELD_NAMES.reasonId);
        const categoryId = getCompensationFieldValue(FIELD_NAMES.categoryId);
        const cabinId = getCompensationFieldValue(FIELD_NAMES.cabinId);

        const compensationPercentageSelected = compensationType.split(' ')[0];
        let voucherSubTypeCode = compensationPercentageSelected ? `percent_${compensationPercentageSelected.replace('%', '')}` : 'percent_NA';
        voucherSubTypeCode = cleanVoucherSubTypeCode(voucherSubTypeCode);
        
        const offlineRequest = {
            requestId: getValidRequestId(),
            requestType: 'OFFLINE',
            quantity: Number(compensationNum),
            flightLegNumber: Number(flightInformationFields[0].value),
            flightMarketingCarrier: 'AC',
            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
            passengerEmailAddress: passengerInformationFields[2].value ? passengerInformationFields[2].value : 'test@c.com',
            passengerFirstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            passengerLastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            pnrLocator: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
            voucherIssuanceComment: comments ? comments : 'Comment',
            voucherIssuanceReason: getVoucherIssuanceReasonText(),
            voucherType: 'NTP',
            voucherSubTypeCode: voucherSubTypeCode,
            categoryId: categoryId || undefined,
            reasonId: reasonId || undefined,
            cabinId: cabinId || undefined,
            createdAt: new Date().toISOString()
        };

        // Save to localStorage in an array, using requestId for uniqueness
        const offlineRequests: any[] = JSON.parse(localStorage.getItem('offlineNtpRequests') ?? '[]');
        offlineRequests.push(offlineRequest);
        localStorage.setItem('offlineNtpRequests', JSON.stringify(offlineRequests));

        // Set a compensation voucher for offline request to show success message
        const offlineVoucher: CompensationVoucher = {
            errorCode: 200,
            errorText: '',
            code: '',
            type: getCompensationFieldValue(FIELD_NAMES.compensationType),
            used: true,
            user: {
                firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                flightMarketingCarrier: 'AC',
                flightMarketingNumber: Number(flightInformationFields[0].value),
                flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                bookingPassengerEmailAddress: passengerInformationFields[2].value,
                voucherIssuanceReason: getVoucherIssuanceReasonText()
            }
        };

        // Set the historyVoucherObj for the history page to list the compensation given
        const historyVoucherObj: CompensationVoucherHistory = {
            id: bookingInformationFields[0].value ? bookingInformationFields[0].value+Math.random() : bookingInformationFields[1].value+Math.random(),
            firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            flightMarketingCarrier: 'AC',
            flightMarketingNumber: Number(flightInformationFields[0].value),
            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
            bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
            bookingPassengerEmailAddress: passengerInformationFields[2].value,
            type: getCompensationFieldValue(FIELD_NAMES.compensationType),
            voucherIssuanceReason: getVoucherIssuanceReasonText(),
            voucherCodes: []
        };

        // Update the history object in localStorage
        const compensationVouchersHistoryObj: CompensationVouchersHistoryObj = JSON.parse(localStorage.getItem('compensationVouchersHistoryObj') ?? '{}');
        if (!compensationVouchersHistoryObj.compensationVouchersHistory || !Array.isArray(compensationVouchersHistoryObj.compensationVouchersHistory)) {
            compensationVouchersHistoryObj.compensationVouchersHistory = [];
        }
        compensationVouchersHistoryObj.compensationVouchersHistory.push(historyVoucherObj);
        localStorage.setItem('compensationVouchersHistoryObj', JSON.stringify(compensationVouchersHistoryObj));

        setCompensationVouchers([offlineVoucher]);
        setShowConfirmation(true);
    };

    const resendErroredWifiCompensation = () => {
        let voucherIdsMutationPayload = {};
        setVoucherIds([resendErroredVoucherId]);
        voucherIdsMutationPayload  = {
            voucherType: 'WIFI',
            passengerLastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            passengerFirstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            flightMarketingCarrier: 'AC',
            flightLegNumber: Number(flightInformationFields[0].value),
            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
            pnrLocator: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
            passengerEmailAddress: passengerInformationFields[2].value ? passengerInformationFields[2].value : 'test@c.com',
            voucherIssuanceReason: getVoucherIssuanceReasonText(),
            voucherIds: [resendErroredVoucherId],
            voucherIssuanceComment: compensationFields[2].value ? compensationFields[2].value : 'Optional',
            requestId: getValidRequestId()
        }

        const performMutation = async (payload) => {
            try {
                const { data } = await myMutation({
                    variables: {
                        payload: payload,
                    },
                });
                if (data) setMutationData(data);
            } catch (e: any) {
                setErrorCode(error.DEFAULT);
            }
        };

        if(voucherIdsMutationPayload && Object.keys(voucherIdsMutationPayload).length) {
            /* 0. get fresh auth token if existing one is expired already */
            const asyncOps = async () => {
                const refreshedToken = await authContext.acquireTokenSilently();
                if (refreshedToken) {
                    performMutation(voucherIdsMutationPayload);
                }
            }
            asyncOps();
        }
    };

    const validateConfirmationPageVoucherErrorNotification = useCallback(() => {
        if (mutationData?.stkAlocateVoucher) {
            const response = mutationData.stkAlocateVoucher;
            
            if (response.code === 6000) {
                // Success case
            setValidationError({
                    isError: false,
                    errorCode: 0
            });
            } else {
                // Error case
            setValidationError({
                isError: true,
                    errorCode: response.code
            });
        }
        }
    }, [mutationData, setValidationError]);

    const validateFormFieldsNoError = () => {
        const bookingInformationInputs = bookingInformationFields.map((f, i) => {
            if (i === 0 && !f.value && !bookingInformationFields[1].value) {
                return {...f, requiredError: t('validations.bookingReference')};
            }
            if (i === 1 && !f.value && !bookingInformationFields[0].value) {
                return {...f, requiredError: t('validations.ticketNumber')};
            }
            return {...f, requiredError: ''};
        });

        const flightInformationInputs = flightInformationFields.map((f, i) => {
            if (i === 0 && !f.value) {
                return {...f, requiredError: t('validations.flightNumber')};
            }
            if (i === 1 && !f.value) {
                return {...f, requiredError: t('validations.date')};
            }
            return {...f, requiredError: ''};
        });

        const passengerInformationInputs = passengerInformationFields.map((f, i) => {
            if (i === 0 && !f.value) {
                return {...f, requiredError: t('validations.firstName')};
            }
            if (i === 1 && !f.value) {
                return {...f, requiredError: t('validations.lastName')};
            }
            if ((i === 2 || i === 3) && !f.value) {
                return {...f, requiredError: t('validations.email')};
            }
            return {...f, requiredError: ''};
        });

        const reasonIdValue = getCompensationFieldValue(FIELD_NAMES.reasonId);
        const categoryIdValue = getCompensationFieldValue(FIELD_NAMES.categoryId);
        const cabinIdValue = getCompensationFieldValue(FIELD_NAMES.cabinId);
        const compensationNumValue = getCompensationFieldValue(FIELD_NAMES.compensationNum);
        const commentsValue = getCompensationFieldValue(FIELD_NAMES.comments);
        const compensationTypeValue = getCompensationFieldValue(FIELD_NAMES.compensationType);
        
        const shouldRequireCabin = (() => {
            if (categoryIdValue) {
                const selectedCategory = masterData.categories.find(c => c.categoryId === categoryIdValue);
                if (selectedCategory && Array.isArray(selectedCategory.cabins)) {
                    return selectedCategory.cabins.length > 0;
                }
            }
            return false;
        })();

        const compensationInputs = compensationFields.map((f) => {
            if (f.name === FIELD_NAMES.reasonId && !f.value) {
                return {...f, requiredError: t('validations.compensationReason')};
            }
            if (f.name === FIELD_NAMES.categoryId && !f.value && categoryIdValue) {
                return {...f, requiredError: ''};
            }
            if (f.name === FIELD_NAMES.cabinId && !f.value && shouldRequireCabin) {
                return {...f, requiredError: t('validations.compensationCabin') || 'Cabin selection is required'};
            }
            if (f.name === FIELD_NAMES.compensationNum && !f.value) {
                return {...f, requiredError: t('validations.compensationsRequested')};
            }
            if (f.name === FIELD_NAMES.comments && !f.value) {
                return {...f, requiredError: t('validations.extraComments') || 'Additional details is required'};
            }
            if (f.name === FIELD_NAMES.compensationType && !f.value) {
                return {...f, requiredError: t('validations.compensationType')};
            }
            if (f.name === FIELD_NAMES.wifiRegion && compensationTypeValue.includes('Wi-Fi') && !f.value) {
                return {...f, requiredError: t('validations.wifiRegion')};
            }
            return {...f, requiredError: ''};
        });

        setBookingInformationFields(bookingInformationInputs);
        setFlightInformationFields(flightInformationInputs);
        setPassengerInformationFields(passengerInformationInputs);
        setCompensationFields(compensationInputs);
        
        if (
            bookingInformationInputs.findIndex(i => i.requiredError) > -1
            || flightInformationInputs.findIndex(i => i.requiredError) > -1
            || passengerInformationInputs.findIndex(i => i.requiredError) > -1
            || compensationInputs.findIndex(i => i.requiredError) > -1
        ) {
            return false;
        }

        if (
            bookingInformationInputs.findIndex(i => i.error) > -1
            || flightInformationInputs.findIndex(i => i.error) > -1
            || passengerInformationInputs.findIndex(i => i.error) > -1
            || compensationInputs.findIndex(i => i.error) > -1
        ) {
            return false;
        }
        return true;
    };

    const validateSuccessfulVoucherResponse = useCallback(() => {
        const voucherCodes: CompensationVoucher[] = [];
        const voucherSuccess = dataVoucherCodes?.requestVouchersById.find(voucher => voucher?.responseStatusCode === 200);

        const historyVoucherObj: CompensationVoucherHistory = {
            id: bookingInformationFields[0].value ? bookingInformationFields[0].value+Math.random() : bookingInformationFields[1].value+Math.random(),
            firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
            flightMarketingCarrier: 'AC',
            flightMarketingNumber: Number(flightInformationFields[0].value),
            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
            bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
            bookingPassengerEmailAddress: passengerInformationFields[2].value,
            type: getCompensationFieldValue(FIELD_NAMES.compensationType),
            voucherIssuanceReason: compensationFields[0].value,
            voucherCodes: []
        };

        for (let dataVoucher of dataVoucherCodes?.requestVouchersById) {
            if (dataVoucher?.responseStatusCode === 200) {
                const voucher = {
                    errorCode: Number(dataVoucher?.responseStatusCode),
                    errorText: dataVoucher?.errorText,
                    code: dataVoucher?.voucherCode,
                    type: getCompensationFieldValue(FIELD_NAMES.compensationType),
                    used: true,
                    user: {
                        firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                        lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                        flightMarketingCarrier: 'AC',
                        flightMarketingNumber: Number(flightInformationFields[0].value),
                        flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                        bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                        bookingPassengerEmailAddress: passengerInformationFields[2].value,
                        voucherIssuanceReason: compensationFields[0].value
                    }
                }
                voucherCodes.push(voucher);
                historyVoucherObj.voucherCodes.push(dataVoucher?.voucherCode);
            }                        
        };

        setCompensationVouchers(voucherCodes);

        if (voucherSuccess) {
            const compensationVouchersHistoryObj: CompensationVouchersHistoryObj = JSON.parse(localStorage.getItem('compensationVouchersHistoryObj') ?? '{}');
            compensationVouchersHistoryObj.compensationVouchersHistory.push(historyVoucherObj);
            localStorage.setItem('compensationVouchersHistoryObj', JSON.stringify(compensationVouchersHistoryObj));
        }    
    }, [
        bookingInformationFields,
        compensationFields,
        dataVoucherCodes?.requestVouchersById,
        flightInformationFields,
        passengerInformationFields,
        setCompensationVouchers
    ]);

    useEffect(() => {
        const handleBeforeUnload = () => sessionStorage.clear();
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    useEffect(() => {
        if (
            authContext.user.roles.length
            && getGroupFromRoles(authContext.user.roles) === GROUPS.inFlight.name
        ) {
            setIsFlightUser(true);
        }

        const initialState = getFields(setResendErroredVoucherId, t);
        setBookingInformationFields([...initialState.bookingInformation]);
        setFlightInformationFields([...initialState.flightInformation]);
        setPassengerInformationFields([...initialState.passengerInformation]);
        setCompensationFields([...initialState.compensationInformation]);
    }, [authContext.user.roles, t]);

    useEffect(() => {
        if (flightInformationFields[0].value) {
            setShowFlightNumberPlaceholder(true);
        }
    }, [flightInformationFields]);

    useEffect(() => {
        if (isDeviceOffline) {
            const compensationType = getCompensationFieldValue(FIELD_NAMES.compensationType);
            setSubmitButtonDisabled(compensationType === 'Wi-Fi')
        } else {
            setSubmitButtonDisabled(false);
        }
    }, [compensationFields, isDeviceOffline]);

    useEffect(() => {
        /* Need exponential call: RETRIES 4 */
        if (retryCount > 0 && retryCount <= 5) {
            const delay = Math.pow(2, retryCount) * 10000;
            const timer = setTimeout(() => {
                const asyncOps = async () => {
                    const refreshedToken = await authContext.acquireTokenSilently();
                    if (refreshedToken) {
                        setVoucherCodeQueryTriggered((prev) => !prev);
                    }
                }
                asyncOps();
            }, delay);
            return () => clearTimeout(timer);;
        }
        else if (retryCount > 5) {
            setLoadingRetry(false);
            setValidationError({
                isError: true,
                errorCode: 4009
            });
        }
        else {
            setLoadingRetry(false);            
        }
    }, [authContext, retryCount, setValidationError]);

    useEffect(() => {
        if (compensationVouchers && compensationVouchers.length) {
            setResendErroredVoucherId('');
            sessionStorage.setItem('RecentFormValues', JSON.stringify({
                [FIELD_NAMES.flightNumber]: flightInformationFields[0].value,
                [FIELD_NAMES.date]: flightInformationFields[1].value,
                [FIELD_NAMES.bookingReference]: bookingInformationFields[0].value,
                [FIELD_NAMES.ticketNumber]: bookingInformationFields[1].value,
                [FIELD_NAMES.firstName]: passengerInformationFields[0].value,
                [FIELD_NAMES.lastName]: passengerInformationFields[1].value,
                [FIELD_NAMES.email]: passengerInformationFields[2].value,
                [FIELD_NAMES.confirmEmail]: passengerInformationFields[3].value
            }));
            setShowConfirmation(true);
        }

    }, [
        bookingInformationFields,
        compensationVouchers,
        flightInformationFields,
        passengerInformationFields,
        setShowConfirmation
    ]);

    useEffect(() => {
        if (!loadingVoucherCodes && !errorVoucherCodes && dataVoucherCodes) {
            setVoucherCodeQueryTriggered(false);
            const statusObj = dataVoucherCodes?.requestStatusById;
            let voucherCodesArr = [];
            let voucherError: number | null = null;
            if (statusObj) {
                // If the data field is a JSON string, parse it
                let parsedData = {};
                try {
                    parsedData = typeof statusObj.data === 'string' ? JSON.parse(statusObj.data) : statusObj.data;
                } catch (e) {
                    parsedData = {};
                }
                // If vouchers are present in parsedData, extract them
                if (
                    parsedData &&
                    typeof parsedData === 'object' &&
                    'vouchers' in parsedData &&
                    Array.isArray((parsedData as any).vouchers)
                ) {
                    voucherCodesArr = (parsedData as any).vouchers
                        .filter((v: any) => v.voucherCode)
                        .map((v: any) => v.voucherCode);
                }
                // If request explicitly failed with a response status code (e.g., 4010 No Wi-Fi codes)
                if (
                    parsedData &&
                    typeof parsedData === 'object' &&
                    (parsedData as any).RequestStatus === 'FAILED' &&
                    (parsedData as any).ResponseStatusCode
                ) {
                    const responseStatusCode = Number((parsedData as any).ResponseStatusCode);
                    if (!Number.isNaN(responseStatusCode) && responseStatusCode !== 200) {
                        voucherError = responseStatusCode;
                    }
                }
                // If error code is present
                if (
                    statusObj.code === 4001 || statusObj.code === 4002 || statusObj.code === 4003 || statusObj.code === 4007 ||
                    statusObj.code === 5001 || statusObj.code === 5002 || statusObj.code === 5003 || statusObj.code === 5004 ||
                    statusObj.code === 5005 || statusObj.code === 5006 || statusObj.code === 5007 || statusObj.code === 5008
                ) {
                    voucherError = statusObj.code;
                }
            }
            if (voucherError) {
                setValidationError({
                    isError: true,
                    errorCode: voucherError
                });
                // Don't retry if we have a specific error code
                setLoadingRetry(false);
                setRetryCount(0); // Reset retry count when we get an error
            } else if (voucherCodesArr.length > 0) {
                setValidationError({
                    isError: false,
                    errorCode: 0
                });
                // Set vouchers for confirmation
                setCompensationVouchers(
                    voucherCodesArr.map(code => ({
                        errorCode: 200,
                        errorText: '',
                        code,
                        type: getCompensationFieldValue(FIELD_NAMES.compensationType),
                        used: true,
                        user: {
                            firstName: passengerInformationFields[0].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            lastName: passengerInformationFields[1].value.replace('-', ' ').replace(REGEX.removeNonAlphaNumeric, '').trim(),
                            flightMarketingCarrier: 'AC',
                            flightMarketingNumber: Number(flightInformationFields[0].value),
                            flightScheduledAt: formatDateInYYYYMMDD(flightInformationFields[1].value),
                            bookingReference: bookingInformationFields[0].value ? bookingInformationFields[0].value : bookingInformationFields[1].value,
                            bookingPassengerEmailAddress: passengerInformationFields[2].value,
                            voucherIssuanceReason: compensationFields[0].value
                        }
                    }))
                );
                setShowConfirmation(true);
            } else {
                setRetryCount(prev => prev + 1);
                setLoadingRetry(true);
            }
        }

        if (errorVoucherCodes) {
            setErrorCode(error.DEFAULT);
        }
    }, [
        dataVoucherCodes,
        errorVoucherCodes,
        loadingVoucherCodes,
        voucherIds.length,
        setErrorCode,
        setValidationError,
        setCompensationVouchers,
        setShowConfirmation,
        compensationFields,
        bookingInformationFields,
        flightInformationFields,
        passengerInformationFields
    ]);

    /* Only call handleOnClickClear if it's not the initial render 
    and if i18n.language has changed */
    useEffect(() => {
        if (previousLanguage && i18n.language !== previousLanguage) {
            handleOnClickClear();
        }
    }, [i18n.language, previousLanguage, handleOnClickClear]);

    return {
        bookingInformationFields,
        compensationFields,
        flightInformationFields,
        isFlightUser,
        loadingRetry,
        loadingMutation,
        loadingVoucherCodes,
        loadingOfflineModule,
        passengerInformationFields,
        resendErroredVoucherId,
        showFlightNumberPlaceholder,
        submitButtonDisabled,
        handleOnClickClear,
        handleOnClickSubmit,
        setBookingInformationFields,
        setCompensationFields,
        setFlightInformationFields,
        setPassengerInformationFields,
        setShowFlightNumberPlaceholder,
        t
    };
};

